import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import axios from 'axios';
import './WeddingInvitation.css';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PDF_VERSION = 3;

const sections = [
  { id: 'invitation', label: 'Invitation', page: 1 },
  { id: 'groom-photo', label: 'The Couple', page: 2 },
  { id: 'ceremony', label: 'Ceremony & Reception', page: 3 },
  { id: 'entourage-1', label: 'Entourage', page: 4 },
  { id: 'entourage-2', label: 'Entourage (cont.)', page: 5 },
  { id: 'dress-code', label: 'Dress Code', page: 6 },
  { id: 'gifts', label: 'Gifts & RSVP', page: 7 },
  { id: 'faq-1', label: 'FAQ', page: 8 },
  { id: 'faq-2', label: 'FAQ (cont.)', page: 9 },
  { id: 'timeline', label: 'Timeline', page: 10 },
  { id: 'bride-photo', label: 'The Couple', page: 11 },
  { id: 'moments', label: 'Moments', page: 12 },
];

const WeddingInvitation = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [pageWidth, setPageWidth] = useState(800);
  const [activeSection, setActiveSection] = useState('invitation');
  const [isNavCollapsed, setIsNavCollapsed] = useState(true);
  const [loading, setLoading] = useState(true);
  const [guestName, setGuestName] = useState('');
  const [visibleSections, setVisibleSections] = useState(new Set(['invitation', 'groom-photo', 'ceremony', 'entourage-1', 'entourage-2', 'dress-code', 'gifts', 'faq-1', 'faq-2', 'timeline', 'bride-photo', 'moments']));
  const sectionRefs = useRef({});

  // Check if guest has accepted RSVP
  useEffect(() => {
    const checkAccess = async () => {
      try {
        const response = await axios.get(`/api/invites/${token}`);
        const invite = response.data;

        // Check if guest has responded and accepted
        if (!invite.hasResponded || invite.attending !== 'yes') {
          navigate('/');
          return;
        }

        setGuestName(invite.guestName);
        setLoading(false);
      } catch (error) {
        // Invalid token or error, redirect to home
        navigate('/');
      }
    };

    checkAccess();
  }, [token, navigate]);

  // Calculate width based on window size
  useEffect(() => {
    // Store initial layout width to detect real resizes vs zoom
    let lastLayoutWidth = window.innerWidth;

    const updateWidth = () => {
      const isMobile = window.innerWidth <= 768;

      if (isMobile) {
        // On mobile, use 90% of screen width with some padding
        const width = Math.min(window.innerWidth * 0.9, 600);
        setPageWidth(width);
      } else {
        // On desktop, account for sidebar
        const sidebar = 280;
        const padding = 100;
        const availableWidth = window.innerWidth - sidebar - padding;
        const width = Math.min(availableWidth, 800);
        setPageWidth(Math.max(width, 400));
      }

      lastLayoutWidth = window.innerWidth;
    };

    let resizeTimer;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const currentWidth = window.innerWidth;

        // Check if this is a zoom event by comparing scale
        // On mobile zoom, visualViewport.width changes but document width doesn't
        const isZoom = window.visualViewport &&
                       Math.abs(window.visualViewport.scale - 1) > 0.01;

        // Only update if it's not a zoom and width changed significantly
        const widthChanged = Math.abs(currentWidth - lastLayoutWidth) > 50;

        if (!isZoom && widthChanged) {
          updateWidth();
        }
      }, 300);
    };

    updateWidth();
    window.addEventListener('resize', handleResize);

    // Also listen to visualViewport resize if available (better for mobile)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      }
      clearTimeout(resizeTimer);
    };
  }, []);

  // Intersection Observer to detect active section and lazy load pages
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '800px 0px 800px 0px', // Load pages well before they come into view
      threshold: 0,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
          // Mark this section as visible for lazy loading
          setVisibleSections(prev => new Set([...prev, entry.target.id]));
        }
      });
    }, options);

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      Object.values(sectionRefs.current).forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  const scrollToSection = (sectionId) => {
    const element = sectionRefs.current[sectionId];
    if (element) {
      const isMobile = window.innerWidth <= 768;
      setIsNavCollapsed(true); // Collapse nav after selecting on mobile

      if (isMobile) {
        // On mobile, scroll with offset to account for fixed navbar
        setTimeout(() => {
          const yOffset = -100; // Negative offset for fixed navbar height
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }, 100);
      } else {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const toggleNav = () => {
    setIsNavCollapsed(!isNavCollapsed);
  };

  if (loading) {
    return (
      <div className="wedding-invitation-container">
        <div className="loading-container">
          <p>Loading invitation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="wedding-invitation-container">
      {/* Sidebar Navigation */}
      <nav className={`wedding-sidebar ${isNavCollapsed ? 'collapsed' : 'expanded'}`}>
        <div className="sidebar-header" onClick={toggleNav}>
          <div className="header-content">
            <img src="/justinelle.jpg" alt="Justine & Janelle" className="logo-image" />
            <div className="header-text">
              <p className="guest-greeting">Welcome, {guestName}!</p>
            </div>
          </div>
          <button className="toggle-nav-btn" aria-label="Toggle navigation">
            <span className={`toggle-icon ${isNavCollapsed ? '' : 'open'}`}>▼</span>
          </button>
        </div>
        <ul className="sidebar-nav">
          {sections.map((section) => (
            <li key={section.id}>
              <button
                className={`nav-item ${activeSection === section.id ? 'active' : ''}`}
                onClick={() => scrollToSection(section.id)}
              >
                <span className="nav-number">{section.page}</span>
                <span className="nav-label">{section.label}</span>
              </button>
            </li>
          ))}
        </ul>
        <div className="sidebar-footer">
          <div className="decorative-leaf"></div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="wedding-content">
        {sections.map((section) => (
          <section
            key={section.id}
            id={section.id}
            ref={(el) => (sectionRefs.current[section.id] = el)}
            className="invitation-section"
          >
            {visibleSections.has(section.id) ? (
              <Document
                file={`/pages/page-${section.page}.pdf?v=${PDF_VERSION}`}
                loading={
                  <div className="pdf-loading">
                    <p>Loading...</p>
                  </div>
                }
                error={
                  <div className="pdf-error">
                    <p>Failed to load page {section.page}</p>
                  </div>
                }
              >
                <Page
                  pageNumber={1}
                  width={pageWidth}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  className="pdf-page"
                />
              </Document>
            ) : (
              <div className="pdf-placeholder" style={{ height: `${pageWidth * 1.414}px` }}>
                <p>Scroll to load...</p>
              </div>
            )}
          </section>
        ))}
      </main>
    </div>
  );
};

export default WeddingInvitation;
